import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Handshake, LogOut, TrendingUp, DollarSign, Award, Users,
  Calendar, BarChart3, Loader2, Shield, Eye, RefreshCw,
} from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("ar-IQ");

export default function PartnerDashboardPage() {
  const [info, setInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [activations, setActivations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const API = `${window.location.origin}/api`;

  const token = localStorage.getItem("partner_token");

  const fetchAll = async () => {
    try {
      setRefreshing(true);
      const headers = { Authorization: `Bearer ${token}` };
      const [d, a] = await Promise.all([
        axios.get(`${API}/partner/dashboard`, { headers }),
        axios.get(`${API}/partner/activations`, { headers, params: { limit: 200 } }),
      ]);
      setInfo(d.data.partner);
      setStats(d.data.stats);
      setActivations(a.data.activations || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("انتهت الجلسة، أعد الدخول");
        localStorage.removeItem("partner_token");
        localStorage.removeItem("partner_info");
        navigate("/partner-login");
      } else {
        toast.error("تعذّر تحميل البيانات");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!token) { navigate("/partner-login"); return; }
    fetchAll();
    const t = setInterval(fetchAll, 60_000); // تحديث كل دقيقة
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const logout = () => {
    localStorage.removeItem("partner_token");
    localStorage.removeItem("partner_info");
    navigate("/partner-login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="w-10 h-10 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 p-4 sm:p-6" dir="rtl" data-testid="partner-dashboard-page">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
            <Handshake className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-white">لوحة الشريك</h1>
            <p className="text-slate-400 text-xs">{info?.name} — {info?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={fetchAll}
            disabled={refreshing}
            variant="outline"
            size="sm"
            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
            data-testid="partner-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 me-1 ${refreshing ? "animate-spin" : ""}`} />
            تحديث
          </Button>
          <Button
            onClick={logout}
            variant="outline"
            size="sm"
            className="border-red-500/40 text-red-300 hover:bg-red-500/10"
            data-testid="partner-logout-btn"
          >
            <LogOut className="w-4 h-4 me-1" />
            خروج
          </Button>
        </div>
      </div>

      {/* Trust Banner */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-center gap-2">
          <Shield className="w-5 h-5 text-emerald-400 shrink-0" />
          <p className="text-emerald-200 text-sm">
            <strong>عرض شفاف ومباشر</strong> — هذه الأرقام مأخوذة لحظياً من سجل التفعيلات الرسمي. لا يمكن لأحد تعديلها أو حذفها بصلاحية الشريك.
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={<Users className="w-6 h-6" />}
          label="إجمالي التراخيص المُفعَّلة"
          value={fmt(stats?.total_activations)}
          color="from-blue-500 to-cyan-500"
          testId="stat-total-activations"
        />
        <StatCard
          icon={<DollarSign className="w-6 h-6" />}
          label="إجمالي الإيرادات"
          value={`${fmt(stats?.total_revenue)} د.ع`}
          color="from-emerald-500 to-teal-500"
          testId="stat-total-revenue"
        />
        <StatCard
          icon={<Award className="w-6 h-6" />}
          label={`حصتك (${stats?.share_percentage || 0}%)`}
          value={`${fmt(stats?.partner_share)} د.ع`}
          color="from-amber-500 to-orange-500"
          highlight
          testId="stat-partner-share"
        />
        <StatCard
          icon={<TrendingUp className="w-6 h-6" />}
          label="تفعيلات كاملة / جزئية"
          value={`${stats?.full_activations || 0} / ${stats?.partial_activations || 0}`}
          color="from-violet-500 to-fuchsia-500"
          testId="stat-activations-breakdown"
        />
      </div>

      {/* Monthly Breakdown */}
      {stats?.monthly_breakdown?.length > 0 && (
        <Card className="max-w-6xl mx-auto mb-6 bg-slate-900/70 border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-emerald-400" />
              <h2 className="text-lg font-bold text-white">توزيع شهري (آخر 12 شهر)</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {stats.monthly_breakdown.map((m) => (
                <div key={m.month} className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <p className="text-xs text-slate-400 mb-1">{m.month}</p>
                  <p className="text-emerald-300 font-bold text-lg">{m.count} ترخيص</p>
                  <p className="text-slate-300 text-sm">{fmt(m.revenue)} د.ع</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activation Table */}
      <Card className="max-w-6xl mx-auto bg-slate-900/70 border-slate-700">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-emerald-400" />
              <h2 className="text-lg font-bold text-white">سجل التفعيلات</h2>
              <span className="bg-emerald-500/20 text-emerald-300 text-xs px-2 py-0.5 rounded-full">
                {activations.length}
              </span>
            </div>
          </div>
          {activations.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>لا توجد تفعيلات بعد</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 text-xs border-b border-slate-700">
                    <th className="text-right py-2 px-2">#</th>
                    <th className="text-right py-2 px-2">المستخدم</th>
                    <th className="text-right py-2 px-2">المبلغ</th>
                    <th className="text-right py-2 px-2">الحالة</th>
                    <th className="text-right py-2 px-2">التاريخ</th>
                    <th className="text-right py-2 px-2">بواسطة</th>
                  </tr>
                </thead>
                <tbody data-testid="activations-table-body">
                  {activations.map((a, i) => {
                    const date = a.activated_at
                      ? new Date(a.activated_at).toLocaleDateString("ar-IQ", { dateStyle: "short" })
                      : "-";
                    const statusColor =
                      a.status === "active" ? "bg-emerald-500/20 text-emerald-300"
                      : a.status === "partial" ? "bg-amber-500/20 text-amber-300"
                      : "bg-slate-500/20 text-slate-300";
                    return (
                      <tr key={i} className="border-b border-slate-800 hover:bg-slate-800/30">
                        <td className="py-2 px-2 text-slate-500">{i + 1}</td>
                        <td className="py-2 px-2 text-slate-200">{a.user_email || a.email || "—"}</td>
                        <td className="py-2 px-2 text-emerald-300 font-bold">{fmt(a.paid_amount)} د.ع</td>
                        <td className="py-2 px-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor}`}>
                            {a.status === "active" ? "كامل" : a.status === "partial" ? "جزئي" : a.status || "—"}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-slate-400 text-xs">{date}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs">{a.activated_by || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ icon, label, value, color, highlight, testId }) {
  return (
    <div
      className={`rounded-xl p-4 border ${highlight ? "border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-orange-500/10" : "border-slate-700 bg-slate-800/50"}`}
      data-testid={testId}
    >
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center text-white mb-2`}>
        {icon}
      </div>
      <p className="text-slate-400 text-xs mb-1">{label}</p>
      <p className="text-white text-xl font-extrabold">{value}</p>
    </div>
  );
}
